import { LitElement, html, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { Coordinate } from 'ol/coordinate'
import '@maersk-global/mds-components-core'
import { dispatchTogglePolarPlotMarkerEvent, PolarChartEvents } from '../helper'
import { WeatherDetails,PolarPlotResponse } from '../types/polar-schema-types'
import { polarChartWrapperStyles } from '../style/polar-chart-styles'
import './polar-plot-cartesian-chart'
import './polar-loading-modal'
import './polar-chart-form'
import './polar-plot'
import './polar-loading-modal'
import '../../voyage-popup/src/voyage-popup'
import {
    defaultZoneVisibility,
    Status,
    ZoneVisibility,
    ZoneVisibilityKey,
} from '../types/polar-chart-types'
import { PolarPlotService } from '../../../src/services/polar-plot-service'
import { VoyagePlan } from '../../../src/models/vessel-planning-model'
import { constructPayload } from '../utils/form-utils'
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils'
import { faro } from '../../../src/utils/rum-utility'
import { VoyageImoModel } from '../../../src/models/voyage-imo-model'
import { ExtractedHeaderType, extractHeadersFromResponse } from '../utils/header-utils'

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement('polar-charts-wrapper')
export class PolarChartsWrapper extends LitElement {
    @property({ attribute: false }) polarRouteCapturedPosition: Coordinate | null = null;
    @property({ attribute: false }) log_id: number | null = null;
    @property({ attribute: false }) polarRouteCapturedPositionETA: string | null = null;
    @property({ type: Object }) plan: VoyagePlan | null = null
    @property({ attribute: false }) polarPlotData: PolarPlotResponse | null = null
    @property({ type: Boolean }) isImminentRiskViewOpen = false;
    @property({ type: Object }) private originalVesselDetails:
        | WeatherDetails['weather']
        | null = null
    @property({ attribute: false }) private errorReport: any = null
    @property({ type: Number }) companyMaxRollAngleLimit: number = 13;

    @state() status : Status = ''
    // reactive states between components
    @state() headingInputValue: number | null = null
    @state() speedInputValue: number | null = null
    @state() responseHeaders: ExtractedHeaderType = null

    @property({ attribute: false }) SWDegree: number | null = null
    @property({ attribute: false }) WDDegree: number | null = null
    @property({ attribute: false }) WVDegree: number | null = null
    @property({ attribute: false }) selectedView: string = 'NorthUp'
    @state() private zoneVisibility: ZoneVisibility = { ...defaultZoneVisibility }
    private shouldReloadAfterEdit = true
    private shouldReloadAfterReset = true
    private isEdited = false
    private lastUpdatedTime: Date | null = null
    lastUpdatedIntervalId: any
    startTime: number = 0;

    static get styles() {
        return [polarChartWrapperStyles]
    }

    constructor() {
        super()
    }

    connectedCallback() {
        super.connectedCallback()
    }

    updated(changedProperties: Map<string, any>) {
        super.updated(changedProperties)

        // refetching vessel details should happen here
        if (changedProperties.has('polarRouteCapturedPosition') && this.polarRouteCapturedPosition && this.status != 'loading') {
            this.isImminentRiskViewOpen = false;
            this._fetchVesselDetails(this.polarRouteCapturedPosition);
                return;
        }

        if(changedProperties.has('log_id') && this.log_id) {
            this.isImminentRiskViewOpen = true;
            this._fetchImminentRiskVesselDetails();
        }
    }

    private handleViewChange(event: CustomEvent) {
        const view = event.detail.view
        this.selectedView = view
    }

    private noActivePlanOrCapturedPosition() {
        this.status = 'error'
        const errorMessage = "The Polar Chart cannot be loaded. You have no active plan or no captured position.";
        const modal = this.renderRoot.querySelector('polar-loading-modal')
        if (modal) {
            ;(modal as any).customErrorText = errorMessage;
        };
        throw new Error('No active plan or captured position');
    }

    // Updated _handleCalculatedData method
    private async _handleCalculatedData(event: CustomEvent) {
        this.status = 'loading'

        const { isReset } = event.detail

        if (!this.plan || !this.polarRouteCapturedPosition || !this.polarPlotData) {
            this.noActivePlanOrCapturedPosition();
            return;
        }

        try {
            const payload = constructPayload(
                event.detail,
                this.plan,
                this.polarRouteCapturedPosition,
                this.polarPlotData,
                isVesselOnCloud ? (VoyageImoModel?.imo ?? this.plan.imoNo): (this.plan.imoNo || (<any>window)['appenv'].vesselIMONumber),
            )

            const response = await PolarPlotService.reCalculatePolarPlotData(payload)
            const headers = extractHeadersFromResponse(response);
            this.responseHeaders = headers;
            const data = response?.data;

            if (data) {
                dispatchTogglePolarPlotMarkerEvent({showMarker: true, position: this.polarRouteCapturedPosition, nearestEta: this.polarRouteCapturedPositionETA});
                this.selectedView = "NorthUp";
                const { weather, forecast, ...polygonData }: PolarPlotResponse = data
                this.polarPlotData = { weather, forecast, ...polygonData }

                // storing edited roll angle zone limit in a separate localStorage key
                const editedRollAngleZoneLimit = event.detail.rollAngleData?.find((d: any) => d.id === 'roll_angle_zone_limit')?.value;
                if (editedRollAngleZoneLimit) {
                    localStorage.setItem('polarPlotEditedRollAngle', editedRollAngleZoneLimit);
                }

                // storing edited acceleration force limit in a separate localStorage key
                const editedAccelerationLimit = event.detail.accelerationForceData?.find((d: any) => d.id === 'acceleration_force_limit')?.value;
                if (editedAccelerationLimit) {
                    localStorage.setItem('polarPlotEditedAccelerationLimit', editedAccelerationLimit);
                }

                if (this.polarPlotData) {
                    this.WVDegree = this.polarPlotData.weather.wave_direction
                    this.WDDegree = this.polarPlotData.weather.wind_direction
                    this.SWDegree = this.polarPlotData.weather.swell_direction
                    this.headingInputValue = this.polarPlotData.course_over_ground || 0
                    this.speedInputValue = this.polarPlotData.speed_over_ground || 0
                }

                // Handle reset logic
                if (isReset) {
                    this._resetFlags()
                } else {
                    this._updateFlags()
                }

                if (this.isEdited) {
                    this._updateFlags()
                    this._resetFlags()
                }

                this.status = 'success'
                this.requestUpdate()
            }
        } catch (error) {
            this.status = 'error'      
            this.errorReport = error;
            this.requestUpdate();
            await this.updateComplete;
            
            const modal = this.renderRoot.querySelector('polar-loading-modal') as any;
            if (modal) {
                modal.errorReport = this.errorReport;
            }
            
            console.error('Failed to recalculate vessel details:', error)
        }
    }

    private _resetFlags() {
        this.isEdited = false
        this.shouldReloadAfterReset = false
        this.requestUpdate() // Force component update
        setTimeout(() => {
            this.shouldReloadAfterReset = true
            this.requestUpdate()
        }, 0)
    }

    private _updateFlags() {
        this.isEdited = false
        this.shouldReloadAfterEdit = false
        this.requestUpdate() // Force component update
        setTimeout(() => {
            this.shouldReloadAfterEdit = true
            this.requestUpdate()
        }, 0)
    }

    async _fetchVesselDetails(polarRouteCapturedPosition: Coordinate | null) {
        this.status = 'loading'
        this.startTime = performance.now();
    
        if (!polarRouteCapturedPosition || !this.plan) {
            this.noActivePlanOrCapturedPosition();
            return;
        }
    
        try {
            const payload = {
                latitude: polarRouteCapturedPosition[1],
                longitude: polarRouteCapturedPosition[0],
                plan_id: this.plan.id,
                imo: isVesselOnCloud ? (VoyageImoModel?.imo ?? this.plan.imoNo): (this.plan.imoNo || (<any>window)['appenv'].vesselIMONumber)
            }
            
            const response = await PolarPlotService.calculatePolarPlotData(payload);
            const headers = extractHeadersFromResponse(response);
            this.responseHeaders = headers;
            const data = response.data;
    
            if (data) {
                localStorage.removeItem('polarPlotEditedRollAngle');
                localStorage.removeItem('polarPlotEditedAccelerationLimit');
                dispatchTogglePolarPlotMarkerEvent({showMarker: true, position: polarRouteCapturedPosition, nearestEta: this.polarRouteCapturedPositionETA});
                this.lastUpdatedTime = new Date();
                this.selectedView = 'NorthUp';
                const { weather, forecast, ...polygonData }: PolarPlotResponse = data
                localStorage.setItem('polarPlotDataWeather', JSON.stringify(weather));
    
                let vessel_max_speed = -1;
                data.maximum_speed_polygon.map((polarPoint: [number, ...any[]]) => {
                    if (polarPoint[0] > vessel_max_speed) {
                        vessel_max_speed = polarPoint[0];
                    }
                });
                
                localStorage.setItem('polarPlotDataVessel', JSON.stringify({
                    speed_over_ground: data.speed_over_ground,
                    course_over_ground: data.course_over_ground,
                    vessel_current_speed: data.speed_over_ground,
                    vessel_heading: data.course_over_ground,
                    solid_gm: data.transverse_metacentric_height_solid,
                    fluid_gm: data.transverse_metacentric_height,
                    roll_angle_zone_limit: this.plan?.rollAngle
                }));
                
                this.originalVesselDetails = weather
                this.polarPlotData = { weather, forecast, ...polygonData }
    
                this.headingInputValue = data.course_over_ground || 0
                this.speedInputValue = data.speed_over_ground || 0
                this.SWDegree = weather?.swell_direction || 0
                this.WDDegree = weather?.wind_direction || 0
                this.WVDegree = weather?.wave_direction || 0
    
                this.status = 'success'
                this.dispatchEvent(new CustomEvent(PolarChartEvents.LoadSuccess));
                this.requestUpdate()
                this.startLastUpdatedTimer();
            }
    
        } catch (error) {
            this.status = 'error'
            this.errorReport = error;
            
            this.requestUpdate();
            await this.updateComplete;
            
            const modal = this.renderRoot.querySelector('polar-loading-modal') as any;
            if (modal) {
                modal.errorReport = this.errorReport;
            }
            
            console.error('Failed to fetch vessel details:', error)
        }
    }

    async _fetchImminentRiskVesselDetails(){
        this.status = 'loading'

        try{
            if(!this.log_id){
                console.log('Log ID is required to fetch imminent risk polar plot data');
                return;
            }

            const response = await PolarPlotService.fetchImminentRiskPolarPlotData(this.log_id);
            const headers = extractHeadersFromResponse(response);
            this.responseHeaders = headers;
            const data = response?.data ?? response;
            const position = data ? [data.longitude, data.latitude] : null;
            if (data) {
                dispatchTogglePolarPlotMarkerEvent({showMarker: true, position: position, nearestEta: this.polarRouteCapturedPositionETA});
                this.lastUpdatedTime = new Date();
                this.selectedView = 'NorthUp';
                const { weather, forecast, ...polygonData }: PolarPlotResponse = data
                localStorage.setItem('polarPlotDataWeather', JSON.stringify(weather));
    
                let vessel_max_speed = -1;
                data.maximum_speed_polygon.map((polarPoint: [number, ...any[]]) => {
                    if (polarPoint[0] > vessel_max_speed) {
                        vessel_max_speed = polarPoint[0];
                    }
                });
                
                localStorage.setItem('polarPlotDataVessel', JSON.stringify({
                    speed_over_ground: data.speed_over_ground,
                    course_over_ground: data.course_over_ground,
                    vessel_current_speed: data.speed_over_ground,
                    vessel_heading: data.course_over_ground,
                    roll_angle_zone_limit: this.plan?.rollAngle
                }));
                
                this.originalVesselDetails = weather
                this.polarPlotData = { weather, forecast, ...polygonData }
    
                this.headingInputValue = data.course_over_ground || 0
                this.speedInputValue = data.speed_over_ground || 0
                this.SWDegree = weather?.swell_direction || 0
                this.WDDegree = weather?.wind_direction || 0
                this.WVDegree = weather?.wave_direction || 0
    
                this.status = 'success'
                this.dispatchEvent(new CustomEvent(PolarChartEvents.LoadSuccess));
                this.dispatchEvent(new CustomEvent(PolarChartEvents.ActiveImminentRisk));
                this.requestUpdate()
                this.startLastUpdatedTimer();
            }
        } catch (error) {
            this.status = 'error'
            this.errorReport = error;
            
            this.requestUpdate();
            await this.updateComplete;
            
            const modal = this.renderRoot.querySelector('polar-loading-modal') as any;
            if (modal) {
                modal.errorReport = this.errorReport;
            }
            
            console.error('Failed to fetch vessel details:', error)
        }
    }
    
    disconnectedCallback() {
        super.disconnectedCallback()
        this.stopLastUpdatedTimer();
    }

    startLastUpdatedTimer() {
        if (this.lastUpdatedIntervalId) {
            clearInterval(this.lastUpdatedIntervalId); 
        }
    
        this.updateLastUpdatedTime();
    }
    
    stopLastUpdatedTimer() {
        if (this.lastUpdatedIntervalId) {
            clearInterval(this.lastUpdatedIntervalId);
            this.lastUpdatedIntervalId = null;
        }
    }
    
    updateLastUpdatedTime() {
        const element = this.renderRoot.querySelector('#lastUpdatedTime') as HTMLElement;
        if (element) {
            const newTime = this.getFormattedLastUpdatedTime();
            if (element.textContent !== newTime) {
                element.textContent = newTime;
            }
        }
    }

    getFormattedLastUpdatedTime(): string {
        if (!this.lastUpdatedTime) {
            return 'Never';
        }
        const now = new Date();
        const diffInMinutes = Math.floor((now.getTime() - this.lastUpdatedTime.getTime()) / 60000);
        return diffInMinutes === 0 ? 'Just now' : diffInMinutes === 1 ? `${diffInMinutes} min ago` : `${diffInMinutes} mins ago`;
    }

    private handleValueChange(event: CustomEvent, type: 'heading' | 'speed') {
        if (type === 'heading') {
            this.headingInputValue = event.detail
        } else {
            this.speedInputValue = event.detail
        }
        this.isEdited = true
    }

    private handleZoneVisibilityChange(event: CustomEvent) {
        const { zone, visible } = event.detail as {
            zone: ZoneVisibilityKey
            visible: boolean
        }

        this.zoneVisibility = {
            ...this.zoneVisibility,
            [zone]: visible,
        }
    }

    private handleBackClick = () => {
        this.dispatchEvent(new Event('back-clicked'))
        dispatchTogglePolarPlotMarkerEvent({showMarker: false, position: null, nearestEta: null});
    }

    private handleRefresh = () => {
        if(this.isImminentRiskViewOpen)
            this._fetchImminentRiskVesselDetails();
        else
            this._fetchVesselDetails(this.polarRouteCapturedPosition);
    }

    render() {
        return this.status === 'success'
            ? html` <div class="polar-plot">
                  <div slot="heading" class="heading">
                      <div class="title-wrapper">
                          <span class="title">Polar Chart Advice </span>
                          <p class="description">
                              Please use the polar chart below to identify the
                              required speed and heading to avoid entering the
                              parametric roll.
                          </p>
                      </div>
                      <div class="heading-status-contents">
                          <div class="last-updated">
                              Last updated on:
                              <div id="lastUpdatedTime" class="color">${this.getFormattedLastUpdatedTime()}</div>
                          </div>
                          <mc-button
                              appearance="neutral"
                              fit="small"
                              @click=${() => this.handleRefresh()}
                              >Refresh</mc-button
                          >
                          ${this.isImminentRiskViewOpen ? html`
                            <mc-tooltip position="right-top">
                            <button
                              class="back-button"
                              slot="trigger"
                              @click=${() => {
                                 this.isImminentRiskViewOpen = false;
                                 this.dispatchEvent(new Event('minimized'))
                                 dispatchTogglePolarPlotMarkerEvent({showMarker: false, position: null, nearestEta: null})}
                                 }
                            >
                                    <mc-icon icon="shrink"></mc-icon>
                                </button>
                                <span>Minimize Polar Chart</span>
                             </mc-tooltip>
                            `: html`
                            <mc-tooltip position="right-top">
                            <button
                              class="back-button"
                              slot="trigger"
                              @click=${() => {
                                    this.handleBackClick()
                                 }}
                            >
                                    <mc-icon icon="times"></mc-icon>
                                </button>
                                <span>Close Polar Chart</span>
                             </mc-tooltip>
                            `}                      
                      </div>
                  </div>

                  <div class="contents">
                      <div class="charts-container">
                          <div class="polar-chart">
                              ${this.shouldReloadAfterReset
                                  ? html`
                                        <voyage-polar-plot
                                            .newDegree=${this.headingInputValue}
                                            .newSpeed=${this.speedInputValue}
                                            .SWDegree=${this.SWDegree}
                                            .WDDegree=${this.WDDegree}
                                            .WVDegree=${this.WVDegree}
                                            .isEdited=${this.isEdited}
                                            .selectedView=${this.selectedView}
                                            .polarPlotData=${this.polarPlotData}
                                            .zoneVisibility=${this.zoneVisibility}
                                            @zone-visibility-change=${this.handleZoneVisibilityChange}
                                        ></voyage-polar-plot>
                                    `
                                  : nothing}
                          </div>
                          <div class="bar-chart">
                              ${this.polarPlotData && this.shouldReloadAfterEdit
                                  ? html`<polar-plot-cartesian-chart
                                        .polarPlotData="${this.polarPlotData}"
                                        .originalVesselDetails="${this
                                            .originalVesselDetails}"
                                        .speedInputValue="${this
                                            .speedInputValue || 0}"
                                        .headingInputValue="${this
                                            .headingInputValue || 0}"
                                        @new-heading=${(e: CustomEvent) =>
                                            this.handleValueChange(
                                                e,
                                                'heading',
                                            )}
                                        @new-speed=${(e: CustomEvent) =>
                                            this.handleValueChange(e, 'speed')}
                                        .isEdited=${this.isEdited}
                                        .zoneVisibility=${this.zoneVisibility}
                                    ></polar-plot-cartesian-chart>`
                                  : nothing}
                          </div>
                      </div>
                      <div class="flex-grow">
                      <div><polar-chart-form .polarPlotData="${this.polarPlotData}" .polarRouteCapturedPosition=${this.polarRouteCapturedPosition} @polar-plot-reset=${() => this.handleRefresh()} @polar-plot-recalculate=${this._handleCalculatedData} @view-changed=${this.handleViewChange} .responseHeaders=${this.responseHeaders} .plan=${this.plan} .companyLimit=${this.companyMaxRollAngleLimit}></polar-chart-form></div>
                      </div>
                  </div>
              </div>`
            : html`<polar-loading-modal
                  @polarplot-retry-calculation=${() => this.handleRefresh()}
                  status=${this.status}
                  .customErrorText=${this.status === 'error' && this.errorReport ? (this.errorReport.error ?? this.errorReport.message ?? this.errorReport.response?.data?.message ?? this.errorReport.response?.data?.error ?? null) : null}
                  .errorReport=${this.errorReport}
                  @close-polarplot-loader=${() => this.handleBackClick()}
              ></polar-loading-modal>`
    }
}
